// Cloud External Files Preview Component

import React, { useEffect, useMemo, useState } from "react";
import { normalizeDate } from "../../FileUploadTab";
import Loader from "@shared/components/Loader";
import { Tooltip } from "flowbite-react";
import StatusIcon from "../Anomaly/StatusIcon";
import { getFileIcon } from "../../FileUploadTab";
import { ServerFile } from "@shared/interfaces/ProjectsFiles";
import { gdriveService } from "@services/GdriveService";
import { oneDriveService } from "@services/OneDriveService";
import FileTableSkeleton from "./FileTableSkeleton";
import { formatDistanceToNow, parseISO, isValid } from "date-fns";
import { enUS } from "date-fns/locale";

export interface ExternalFileData {
  last_sync: { google?: string; microsoft?: string };
  total_count: number;
  files: ServerFile[];
}

interface ExternalFilePreviewProps {
  projectId: string;
  loadFile: (id: string, type: string, size: number, filename: string) => void;

  handleCheckboxChange?: (fileId: string) => void;

  checkedItems: string[];
  setCheckedItems: React.Dispatch<React.SetStateAction<string[]>>;

  loadingFileId: string | null;
  activeTaskFileIds: string[];
  handleShowPopup: (fileId: string) => void;
  triggerStatusPopup: (severity: string, anomaly_count: number) => void;
  clearStatusPopup: () => void;
  refreshFile: number;
  loadFiles: () => Promise<void>;
  data: ExternalFileData;
  cloudType?: "gdrive" | "onedrive";
}

const ExternalFilePreview: React.FC<ExternalFilePreviewProps> = ({
  projectId,
  loadFile,
  checkedItems,
  setCheckedItems,
  loadingFileId,
  activeTaskFileIds,
  handleShowPopup,
  triggerStatusPopup,
  clearStatusPopup,
  loadFiles,
  data,
  refreshFile,
  cloudType = "gdrive",
}) => {
  const apiService = cloudType === "gdrive" ? gdriveService : oneDriveService;
  const cloudIcon =
    cloudType === "gdrive" ? "/icon_google_cloud.svg" : "/icon_ms_onedrive.svg";

  const files = data.files || [];
  const lastSync =
    (cloudType === "gdrive"
      ? data.last_sync?.google
      : data.last_sync?.microsoft) || "";
  const totalCount = data.total_count || 0;

  const [isOpen, setIsOpen] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const getLastSyncedTime = (stamp: string): string => {
    if (!stamp) return "Never synced";
    const parsedDate = parseISO(`${stamp}Z`);
    return isValid(parsedDate)
      ? formatDistanceToNow(parsedDate, { addSuffix: true, locale: enUS })
      : "Invalid date";
  };

  const toggleOpen = async () => {
    if (!isOpen && files.length === 0) await loadFiles();
    setIsOpen(!isOpen);
  };

  const handleSync = async () => {
    try {
      setLoading(true);
      await apiService.syncProject(projectId);
      await loadFiles();
      setIsOpen(true);
    } catch (error) {
      console.error("Failed to sync external files:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredFiles = useMemo(
    () =>
      files.filter((file) =>
        file.filename.toLowerCase().includes(search.toLowerCase())
      ),
    [files, search]
  );

  const readyFiles = filteredFiles.filter((f) => f.status !== "processing");
  const checkItemsExt = checkedItems.filter((id) =>
    id.endsWith("-gdrive-ext-file")
  );

  const toggleAll = () => {
    if (checkItemsExt.length === readyFiles.length) {
      setCheckedItems((prev) =>
        prev.filter((id) => !id.endsWith("-gdrive-ext-file"))
      );
    } else {
      const fileIds = readyFiles.map((f) => `${f.id}-gdrive-ext-file`);
      setCheckedItems((prev) => [...prev, ...fileIds]);
    }
  };

  const allChecked =
    checkItemsExt.length !== 0
      ? checkItemsExt.length === readyFiles.length
      : false;

  // Toggle a single id (write-through to parent)
  const toggleOne = (id: string) => {
    const isSelected = checkedItems.includes(id);
    const next = isSelected
      ? checkedItems.filter((x) => x !== id)
      : [...checkedItems, id];
    setCheckedItems(next);
  };

  useEffect(() => {
    if (isOpen) {
      loadFiles();
    }
  }, [refreshFile]);

  return (
    <div className="border-2 border-[#E4E7EC] rounded-lg overflow-hidden mb-4">
      <div className="w-full bg-[#F9FAFB] flex items-center justify-between px-4 py-4">
        <div className="flex items-center space-x-2">
          <img src={cloudIcon} className="w-6" alt="Drive" />
          <h2 className="text-sm 2xl:text-base font-semibold text-[#182637]">
            External Files
            <span className="ml-1 text-gray-500 font-normal text-xs 2xl:text-sm">
              ({totalCount} {totalCount === 1 ? "document" : "documents"})
            </span>
          </h2>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleSync}
            type="button"
            className="text-xs 2xl:text-sm  h-8 flex space-x-1 items-center justify-center text-gray-500 hover:text-gray-700 bg-white px-3 border border-gray-200 rounded-md"
          >
            <span>{getLastSyncedTime(lastSync || "")}</span>
            <img src="/refresh-ccw.svg" className="w-3 h-3" alt="" />
          </button>
          {isOpen && files.length > 0 && (
            <input
              type="text"
              placeholder="Search document..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-72 border border-gray-200 rounded-md px-3 h-8 text-sm text-gray-500 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          )}
          <img
            src={
              isOpen
                ? "/arrow_dropdown_black_up.svg"
                : "/arrow_dropdown_black.svg"
            }
            className="w-4 cursor-pointer"
            onClick={toggleOpen}
          />
        </div>
      </div>

      {loading && <FileTableSkeleton />}

      {isOpen && !loading && (
        <>
          {filteredFiles.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">
              {search.length > 0
                ? `No documents found matching "${search}".`
                : "No external documents found."}
            </div>
          ) : (
            <table className="w-full table-auto text-sm 2xl:text-base">
              <thead>
                <tr className="text-left border-b-2 border-[#f9fafb]">
                  <th className="py-3 px-4">
                    {/* Section-scoped Select All */}
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="py-3 px-4 text-slate-700">Document</th>
                  <th className="py-3 px-4 text-slate-700">Type</th>
                  <th className="py-3 px-4 text-slate-700">Size</th>
                  <th className="py-3 px-4 text-slate-700">User</th>
                  <th className="py-3 px-4 text-slate-700">Date</th>
                  <th className="py-3 px-4 text-slate-700 text-center">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map((file) => {
                  const isDisabled = !["ready", "failed"].includes(
                    file.status.toLowerCase()
                  );
                  const isChecked = checkedItems.includes(
                    `${file.id}-gdrive-ext-file`
                  );

                  return (
                    <tr
                      key={file.id}
                      className={`transition-colors duration-200 border-b-2 border-[#f9fafb] group ${
                        isDisabled
                          ? "bg-gray-100 text-gray-400 cursor-wait opacity-60"
                          : hoveredRow === `${file.id}-gdrive-ext-file` ||
                            isChecked
                          ? "bg-[#f9fafb]"
                          : "bg-white"
                      }`}
                      onMouseEnter={() =>
                        !isDisabled &&
                        setHoveredRow(`${file.id}-gdrive-ext-file`)
                      }
                      onMouseLeave={() => !isDisabled && setHoveredRow(null)}
                      onClick={() => {
                        if (!isDisabled)
                          toggleOne(`${file.id}-gdrive-ext-file`);
                      }}
                    >
                      <td className="py-3 px-4">
                        {(hoveredRow === `${file.id}-gdrive-ext-file` ||
                          isChecked) && (
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleOne(`${file.id}-gdrive-ext-file`);
                            }}
                          />
                        )}
                      </td>
                      <td
                        className={`py-3 px-4 ${isChecked ? "font-bold" : ""}`}
                      >
                        <div className="flex text-slate-700 items-center my-1 space-x-3">
                          {loadingFileId === `${file.id}` ? (
                            <Loader color="#7da4f8" />
                          ) : (
                            <img
                              src={getFileIcon(file.filetype)}
                              alt="icon"
                              className="w-7 h-7"
                            />
                          )}
                          <div className="relative flex-1">
                            <div
                              className={`flex space-x-2 truncate w-full ${
                                isDisabled
                                  ? "cursor-wait text-gray-400"
                                  : "cursor-pointer hover:underline"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation(); // open without toggling row
                                if (!isDisabled) {
                                  loadFile(
                                    file.id,
                                    file.filetype,
                                    file.size,
                                    file.filename
                                  );
                                }
                              }}
                            >
                              <span className="truncate">{file.filename}</span>
                            </div>
                            {isDisabled && (
                              <div className="absolute bottom-full left-[40px] mb-1 hidden group-hover:block z-20">
                                <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg">
                                  Processing
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-700">
                        {file.filetype}
                      </td>
                      <td className="py-3 px-4 text-slate-700">
                        {(file.size / 1024).toFixed(2)} KB
                      </td>
                      <td className="py-3 px-4 text-slate-700">
                        {file.owner?.first_name} {file.owner?.last_name}
                      </td>
                      <td className="py-3 px-4 text-slate-700">
                        {file.uploaded_at
                          ? normalizeDate(file.uploaded_at)
                          : ""}
                      </td>
                      <td className="py-3 px-4 text-slate-700">
                        <div className="flex justify-center items-center">
                          {file.status === "failed" ? (
                            <Tooltip
                              content="File Processing Failed"
                              arrow={false}
                            >
                              <img
                                src="/status/x-circle.svg"
                                className="w-5 h-5 2xl:w-6 2xl:h-6"
                              />
                            </Tooltip>
                          ) : ["xlsx", "csv"].includes(
                              file.filetype.toLowerCase()
                            ) ? (
                            file.status === "ready" ? (
                              activeTaskFileIds.includes(file.id) ? (
                                <Tooltip
                                  content="Looking for Anomalies"
                                  arrow={false}
                                >
                                  <Loader color="#7da4f8" />
                                </Tooltip>
                              ) : (
                                <StatusIcon
                                  file={file}
                                  handleShowPopup={handleShowPopup}
                                  triggerStatusPopup={triggerStatusPopup}
                                  clearStatusPopup={clearStatusPopup}
                                />
                              )
                            ) : (
                              <Loader color="#7da4f8" />
                            )
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default ExternalFilePreview;
